import React, { useEffect, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import {
  Button,
  Banner,
  Checkbox,
  Spinner,
  FieldBox,
  NativeInput,
} from '@pushwoosh/dumb-components';
import { Color } from '@pushwoosh/kit-constants';
import { Horizontal, Vertical } from '@pushwoosh/kit-helpers';
import { H1, Text } from '@pushwoosh/kit-typography';

import { useEnvironment } from '~/src/contexts/environment';
import { browserTimezone } from '~/src/helpers/common.helpers';
import { RouterLink } from '~/src/ui-kit';
import { ButtonLoader } from '~/src/components/ButtonLoader';
import { RECAPTCHA_ACTIONS, useRecaptcha } from '~/src/components/Recaptcha';
import { Separator } from '~/src/components/Separator';
import { GoogleLogo } from '~/src/components/GoogleLogo';
import { validateEmail } from '~/src/helpers/validation';
import { ProductLogo } from '~/src/components/Logo';
import { LanguageSelect } from '~/src/components/LanguageSelect';
import { AgreementText } from '~/src/components/AgreementText';
import { Lang, useLang } from '~/src/polyglot';

import productScreenImage from './assets/productScreen.png';
import type { RegisterPageProps, FormValues } from './Register.types';

import {
  RootBox,
  SideBox,
  SideContent,
  BackgroundBox,
  ImageWrapper,
  ImageBox,
  ProductImage,
  FormBox,
  TitleBox,
  InputFieldsBox,
} from './styles';

export function RegisterPage({
  inviteCode,
  inviteEmail,
  isLoadedCountry,
  isLoadedInviteEmail,
  errorMessage,
  plan,
  registerHandler,
}: RegisterPageProps): ReactElement {
  const { getLinkByName, productName } = useEnvironment();
  const { getRecaptchaToken } = useRecaptcha(productName);
  const lang = useLang();

  const isAllowGoogleSignUp = productName === 'Pushwoosh';

  const googleSignUpParams = new URLSearchParams();
  if (inviteCode) googleSignUpParams.set('invite', inviteCode);
  if (plan) googleSignUpParams.set('plan', plan);
  googleSignUpParams.set('timezone', browserTimezone());
  const googleSignUpQuery = googleSignUpParams.toString();
  const googleSignUpUrl = `https://user-api.svc-nue.pushwoosh.com/register-with-google${googleSignUpQuery ? `?${googleSignUpQuery}` : ''}`;

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    setValue,
    setError,
    clearErrors,
  } = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      agreement: false,
    },
  });

  useEffect(() => {
    setValue('email', inviteEmail);
  }, [inviteEmail, setValue, getValues, setError]);

  async function onSubmit(values: FormValues): Promise<void> {
    if (!values.agreement) {
      setError('agreement', {
        type: 'required',
        message: lang({ message: 'REGISTER.ERROR.MUST_ACCEPT_TERMS' }),
      });
    } else {
      await registerHandler({
        token: await getRecaptchaToken(RECAPTCHA_ACTIONS.register),
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
      });
    }
  }

  return (
    <RootBox>
      <SideBox>
        <ProductLogo format="horizontal" />
        <SideContent>
          <FormBox onSubmit={handleSubmit(onSubmit)}>
            <TitleBox>
              <H1><Lang message="REGISTER.TITLE" /></H1>
              <span>
                <Lang message="REGISTER.SUBTITLE" />
              </span>
            </TitleBox>
            <InputFieldsBox>
              <FieldBox
                title={lang({ message: 'REGISTER.FIELD.EMAIL' })}
                error={!!errors.email && (
                  <Text $variant="footnote" $color={Color.DANGER}>
                    <Lang message="REGISTER.ERROR.INVALID_EMAIL" />
                  </Text>
                )}
              >
                <NativeInput
                  autoFocus
                  $isErrored={!!errors.email}
                  disabled={!isLoadedInviteEmail || inviteEmail !== ''}
                  placeholder={!isLoadedInviteEmail ? lang({ message: 'REGISTER.PLACEHOLDER.LOADING' }) : ''}
                  {...register('email', { required: true, validate: validateEmail })}
                />
              </FieldBox>
              <FieldBox
                title={lang({ message: 'REGISTER.FIELD.FIRST_NAME' })}
                error={!!errors.firstName && (
                  <Text $variant="footnote" $color={Color.DANGER}>
                    <Lang message="REGISTER.ERROR.FIRST_NAME" />
                  </Text>
                )}
              >
                <NativeInput
                  $isErrored={!!errors.firstName}
                  {...register('firstName', { required: true })}
                />
              </FieldBox>
              <FieldBox
                title={lang({ message: 'REGISTER.FIELD.LAST_NAME' })}
                error={!!errors.lastName && (
                  <Text $variant="footnote" $color={Color.DANGER}>
                    <Lang message="REGISTER.ERROR.LAST_NAME" />
                  </Text>
                )}
              >
                <NativeInput
                  $isErrored={!!errors.lastName}
                  {...register('lastName', { required: true })}
                />
              </FieldBox>
              <FieldBox
                isOptional
                title={lang({ message: 'REGISTER.FIELD.PHONE' })}
                error={!!errors.phone && (
                  <Text $variant="footnote" $color={Color.DANGER}>
                    <Lang message="REGISTER.ERROR.PHONE" />
                  </Text>
                )}
              >
                <NativeInput
                  $isErrored={!!errors.phone}
                  {...register('phone')}
                />
              </FieldBox>
              <FieldBox
                tooltip={errors.agreement && errors.agreement.message}
                error={!!errors.agreement && (
                  <Horizontal $margin="0 0 0 24px">
                    <Text $variant="footnote" $color={Color.DANGER}>
                      <Lang message="REGISTER.ERROR.MUST_ACCEPT_TERMS" />
                    </Text>
                  </Horizontal>
                )}
              >
                <Checkbox
                  name="agreement"
                  label={(
                    <AgreementText>
                      <Lang message="REGISTER.AGREEMENT" />
                    </AgreementText>
                  )}
                  isChecked={watch('agreement')}
                  onChange={(): void => {
                    const newValue = !watch('agreement');
                    setValue('agreement', newValue, { shouldValidate: true });

                    if (newValue) {
                      clearErrors('agreement');
                    }
                  }}
                />
              </FieldBox>
              {!!errorMessage && !isSubmitting && <Banner type="error" title={errorMessage} />}
            </InputFieldsBox>
            <Vertical $gap={12}>
              {isLoadedCountry ? (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    isLoading={isSubmitting}
                  >
                    <Lang message="REGISTER.BUTTON.SIGN_UP" />
                  </Button>
                  {isAllowGoogleSignUp && (
                    <>
                      <Separator />
                      {isSubmitting ? (
                        <ButtonLoader>
                          <Horizontal $alignItems="center" $gap={6}>
                            <GoogleLogo opacity="50%" />
                            <Lang message="REGISTER.BUTTON.GOOGLE" />
                          </Horizontal>
                        </ButtonLoader>
                      ) : (
                        <Button
                          as="a"
                          type="button"
                          color="secondary"
                          href={googleSignUpUrl}
                        >
                          <Horizontal $alignItems="center" $gap={6}>
                            <GoogleLogo />
                            <Lang message="REGISTER.BUTTON.GOOGLE" />
                          </Horizontal>
                        </Button>
                      )}
                    </>
                  )}
                </>
              ) : (
                <ButtonLoader>
                  <Spinner size="small" />
                  <Lang message="REGISTER.LOADING" />
                </ButtonLoader>
              )}
            </Vertical>
            <Horizontal $alignItems="center" $justifyContent="center" $gap={4}>
              <Lang message="REGISTER.FOOTER.HAVE_ACCOUNT" />
              <RouterLink title={lang({ message: 'REGISTER.LINK.SIGN_IN' })} to={getLinkByName('login')}>
                <Lang message="REGISTER.LINK.SIGN_IN" />
              </RouterLink>
            </Horizontal>
          </FormBox>
        </SideContent>
      </SideBox>
      <BackgroundBox>
        <ImageWrapper>
          <ImageBox>
            <ProductImage alt="Pushwoosh" src={productScreenImage} />
          </ImageBox>
        </ImageWrapper>
      </BackgroundBox>
      <LanguageSelect />
    </RootBox>
  );
}
